import type { FC, ReactNode } from 'react'
import type { RegisterUpdater } from '@/components/landing/film/overlay-utils'

import { useEffect, useRef } from 'react'

import {
    SCENES,
    ease,
    formatTimecode,
    progress,
    sceneIndexAt
} from '@/components/landing/film/timeline'

import {
    fixed,
    setStyle,
    setText
} from '@/components/landing/film/overlay-utils'

interface FilmChromeProps {
    register: RegisterUpdater
}

const CORNERS = [
    'left-[3%] top-[5%] border-l border-t',
    'right-[3%] top-[5%] border-r border-t',
    'left-[3%] bottom-[5%] border-l border-b',
    'right-[3%] bottom-[5%] border-r border-b'
]

// Camera viewfinder chrome: letterbox, corner marks, timecode and scene slate
const FilmChrome: FC<FilmChromeProps> = ({ register }): ReactNode => {
    const topBarRef = useRef<HTMLDivElement>(null)
    const bottomBarRef = useRef<HTMLDivElement>(null)
    const sceneRef = useRef<HTMLSpanElement>(null)
    const timecodeRef = useRef<HTMLSpanElement>(null)
    const formatRef = useRef<HTMLSpanElement>(null)
    const hudRef = useRef<HTMLDivElement>(null)

    useEffect(() => register((t, _real, frame) => {
        const open = ease.inOutCubic(progress(t, 0.05, 1.15))
        const rest = frame.portrait ? 0 : 2.4
        const bar = 50 - (50 - rest) * open
        setStyle(topBarRef.current, 'height', `${fixed(bar, 2)}%`)
        setStyle(bottomBarRef.current, 'height', `${fixed(bar, 2)}%`)
        setStyle(hudRef.current, 'opacity', fixed(progress(t, 0.6, 1.4) * 0.9, 3))

        const scene = SCENES[sceneIndexAt(t)]
        setText(sceneRef.current, `SC ${scene.number} · ${scene.title}`)
        setText(timecodeRef.current, formatTimecode(t))
        setText(formatRef.current, frame.portrait ? 'Vertical 9:16' : 'Cinema 16:9')
    }), [register])

    return (
        <div className='pointer-events-none absolute inset-0' aria-hidden='true'>
            <div className='absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_45%,rgba(3,4,6,0.72)_100%)]' />
            <div className='film-grain' />

            <div ref={hudRef} className='absolute inset-0' style={{ opacity: 0 }}>
                {CORNERS.map((corner) => (
                    <span key={corner} className={`absolute size-[2.6cqmin] border-titanium/60 ${corner}`} />
                ))}

                <div className='film-mono absolute left-[5.5%] top-[7%] flex items-center gap-3 text-titanium-light'>
                    <span className='size-1.5 animate-pulse rounded-full bg-gold' />
                    <span ref={sceneRef}>SC 01 &middot; Opener</span>
                    <span className='hidden text-titanium sm:inline' ref={timecodeRef}>00:00:00</span>
                </div>

                <div className='film-mono absolute right-[5.5%] top-[7%] hidden text-right text-titanium-light sm:block'>
                    <span ref={formatRef}>Cinema 16:9</span>
                    <span className='text-titanium'> &middot; 60 FPS</span>
                </div>
            </div>

            <div ref={topBarRef} className='absolute inset-x-0 top-0 bg-black' style={{ height: '50%' }} />
            <div ref={bottomBarRef} className='absolute inset-x-0 bottom-0 bg-black' style={{ height: '50%' }} />
        </div>
    )
}

export default FilmChrome
